// src/Pages/User/ChatbotSettings/FirstUser.js
import React, { useState } from "react";
import { Button, Input, Steps, Typography, Avatar, message } from "antd";
import { motion } from "framer-motion";
import { PlusOutlined, ArrowRightOutlined, UserOutlined } from "@ant-design/icons";
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";
import "./assets/css/ChatbotSetting.css";
import config from "../../../config";
import CustomLayout from "../../../layouts/Customlayout";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import setupAnimation from "./assets/Animation-setup.json";
import typingAnimation from "./assets/Animation-typing.json";
import customizationAnimation from "./assets/Animation -customization.json";
import featuresAnimation from "./assets/Animation -features.json";
import successAnimation from "./assets/Animation -success.json";

const { Title } = Typography;

const ChatbotSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [chatbotName, setChatbotName] = useState("Jaweb Chatbot");
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [initialMessage, setInitialMessage] = useState(t("chatbotSetup.messages.initialPlaceholder"));
  const [sections, setSections] = useState([{ id: 1, value: "" }]);
  const [Loader, setLoader] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#7F28F8");
  const [humanTakeover, setHumanTakeover] = useState(false);
  const [toggleEmailSummary, setToggleEmailSummary] = useState(false);

  const steps = [
    { title: t("chatbotSetup.steps.basicInfo"), description: t("chatbotSetup.steps.basicInfoDesc") },
    { title: t("chatbotSetup.steps.messages"), description: t("chatbotSetup.steps.messagesDesc") },
    { title: t("chatbotSetup.steps.appearance"), description: t("chatbotSetup.steps.appearanceDesc") },
    { title: t("chatbotSetup.steps.features"), description: t("chatbotSetup.steps.featuresDesc") },
    { title: t("chatbotSetup.steps.preview"), description: t("chatbotSetup.steps.previewDesc") },
  ];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSaveBtn = () => {
    setLoader(true);

    const userSuggestions = sections.map((s) => s.value).filter((v) => v.trim());
    const fd = new FormData();
    fd.append("chatbot_color", selectedColor);
    fd.append("chatbot_initial_msg", initialMessage);
    fd.append("disable_chatbot_globally", humanTakeover);
    fd.append("enable_chat_summary", toggleEmailSummary);
    if (imageFile) fd.append("chatbot_logo", imageFile);
    fd.append("firstLogin", false);
    if (userSuggestions.length) fd.append("user_questions", JSON.stringify(userSuggestions));

    axios
      .post(`${config.apiUrl}chatbot-details/`, fd, {
        headers: {
          Authorization: `Token ${localStorage.getItem("token")}`,
          Accept: "application/json",
        },
      })
      .then(() => {
        message.success(t("chatbotSetup.messagesStatus.saveSuccess"));
        setLoader(false);
        window.location.href = "https://crm.jaweb.me/user/settings";
      })
      .catch(() => {
        message.error(t("chatbotSetup.messagesStatus.saveError"));
        setLoader(false);
      });
  };

  return (
    <div className="setup-container">
      <Steps current={currentStep} className="steps-bar">
        {steps.map((step, idx) => (
          <Steps.Step key={idx} title={step.title} description={step.description} />
        ))}
      </Steps>

      <motion.div
        key={currentStep}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="step-content"
      >
        {currentStep === 0 && (
          <>
            <Lottie animationData={setupAnimation} style={{ width: 200, margin: "auto" }} />

            <Title level={3}>{t("chatbotSetup.basic.stepTitle")}</Title>
            <div className="mb-4">
              <span className="text-gray-500 text-[19px]">{t("chatbotSetup.basic.stepSubtitle")}</span>
            </div>
            <Input
              placeholder={t("chatbotSetup.basic.namePlaceholder")}
              className="p-2"
              value={chatbotName}
              onChange={(e) => setChatbotName(e.target.value)}
            />

            <div className="mt-6">
              <Title level={3}>{t("chatbotSetup.basic.imageTitle")}</Title>
              <div className="mb-4">
                <span className="text-gray-500 text-[19px]">{t("chatbotSetup.basic.imageSubtitle")}</span>
              </div>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                id="upload-input"
                onChange={handleImageChange}
              />
              <label htmlFor="upload-input">
                <Avatar
                  size={128}
                  src={previewImage || undefined}
                  style={{
                    border: `3px solid ${selectedColor}`,
                    backgroundColor: "#f0f0f0",
                    cursor: "pointer",
                    marginBottom: 10,
                  }}
                >
                  {!previewImage && <UserOutlined style={{ color: "black" }} />}
                </Avatar>
              </label>
            </div>
          </>
        )}

        {currentStep === 1 && (
          <>
            <Lottie animationData={typingAnimation} style={{ width: 200, margin: "auto" }} />

            <Title level={3}>{t("chatbotSetup.messages.stepTitle")}</Title>
            <div className="mb-4">
              <span className="text-gray-500 text-[19px]">{t("chatbotSetup.messages.stepSubtitle")}</span>
            </div>
            <Input
              placeholder={t("chatbotSetup.messages.initialPlaceholder")}
              className="p-2 mb-4"
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
            />

            <Title level={3}>{t("chatbotSetup.messages.quickRepliesTitle")}</Title>
            <div className="mb-4">
              <span className="text-gray-500 text-[19px]">{t("chatbotSetup.messages.quickRepliesSubtitle")}</span>
            </div>
            {sections.map((section, idx) => (
              <Input
                key={section.id}
                className="mt-2"
                placeholder={t("chatbotSetup.messages.quickReplyPlaceholder")}
                value={section.value}
                onChange={(e) => {
                  const updated = [...sections];
                  updated[idx].value = e.target.value;
                  setSections(updated);
                }}
              />
            ))}
            <Button
              className="mt-4"
              icon={<PlusOutlined />}
              onClick={() => setSections([...sections, { id: sections.length + 1, value: "" }])}
            >
              {t("chatbotSetup.messages.addSuggestion")}
            </Button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Lottie animationData={customizationAnimation} style={{ width: 200, margin: "auto" }} />

            <Title level={3}>{t("chatbotSetup.appearance.stepTitle")}</Title>
            <div className="mb-4">
              <span className="text-gray-500 text-[19px]">{t("chatbotSetup.appearance.stepSubtitle")}</span>
            </div>
            <input
              type="color"
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
              className="color-picker w-[50%]"
            />
          </>
        )}

        {currentStep === 3 && (
          <>
            <Lottie animationData={featuresAnimation} style={{ width: 200, margin: "auto" }} />

            <Title level={3}>{t("chatbotSetup.features.stepTitle")}</Title>
            <div className="mb-4">
              <span className="text-gray-500 text-[19px]">{t("chatbotSetup.features.stepSubtitle")}</span>
            </div>
            <div className="flex gap-6 justify-center items-center">
              <label>
                <input
                  type="checkbox"
                  className="w-6 h-6"
                  checked={humanTakeover}
                  onChange={() => setHumanTakeover(!humanTakeover)}
                />{" "}
                {t("chatbotSetup.features.humanTakeover")}
              </label>
              <label>
                <input
                  type="checkbox"
                  className="w-6 h-6"
                  checked={toggleEmailSummary}
                  onChange={() => setToggleEmailSummary(!toggleEmailSummary)}
                />{" "}
                {t("chatbotSetup.features.emailSummary")}
              </label>
            </div>
          </>
        )}

        {currentStep === 4 && (
          <div className="flex flex-col justify-center items-center">
            <Title level={3}>{t("chatbotSetup.preview.stepTitle")}</Title>
            <div
              className="chat-box"
              style={{ border: `1px solid ${selectedColor}`, width: "50%" }}
            >
              <div className="header_chat" style={{ background: selectedColor }}>
                <img
                  src={
                    previewImage ||
                    "https://www.shutterstock.com/image-vector/chat-bot-logo-design-concept-600nw-1938811039.jpg"
                  }
                  alt="Logo"
                />
              </div>
              <div className="messages-container">
                <div className="message business-message">
                  <p className="initialMsg_text">{initialMessage}</p>
                </div>
              </div>
              {sections.some((s) => s.value.trim()) && (
                <div className="premadechat-wrapper-new">
                  {sections.map((s) =>
                    s.value.trim() ? (
                      <div className="conversation-item-text-new" key={s.id}>
                        <p>{s.value}</p>
                      </div>
                    ) : null
                  )}
                </div>
              )}
              <div className="input-container">
                <input type="text" style={{ border: `1px solid ${selectedColor}` }} />
                <button type="button">
                  <ArrowRightOutlined style={{ color: selectedColor }} />
                </button>
              </div>
              <div className="flex items-center justify-center">
                <a href="https://jaweb.me/" target="_blank" rel="noopener noreferrer">
                  <span className="text-gray-500">{t("chatbotSetup.poweredBy")}</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="button-group">
        {currentStep > 0 && <Button onClick={prevStep}>{t("chatbotSetup.buttons.back")}</Button>}
        {currentStep < steps.length - 1 ? (
          <Button
            type="primary"
            className="bg-[#7F28F8]"
            onClick={nextStep}
            icon={<ArrowRightOutlined />}
          >
            {t("chatbotSetup.buttons.next")}
          </Button>
        ) : (
          <Button
            loading={Loader}
            className="bg-[#7F28F8]"
            type="primary"
            onClick={handleSaveBtn}
          >
            {t("chatbotSetup.buttons.saveFinish")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatbotSetup;
